import moment from 'moment';
import { getCropZonesFromProperties } from 'screens/Reports/helpers/helperFunctions';
import {
  Product,
  ProductQuantityType,
  ApplicationMethod,
  Property
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { OperationPropertyVO } from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';
import {
  getPeopleByIds,
  getCompaniesByIds
} from 'screens/Applications&WorkOrders/Components/OperationPdf/helpers/apiHelpers';
import {
  applicationMethodOptions,
  timingEventOptions
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/selectOptions';
import { PersonCompany } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { Maths } from 'helpers/maths';
import { RecommendationVO, RecommendationProductEntry } from './types';

export const mapTimingEventToValue = (timingEvent?: string): string => {
  if (!timingEvent) return '';
  const timingEventOption = timingEventOptions.find(
    option => option.label === timingEvent
  );
  return timingEventOption?.value ?? '';
};

export const mapApplicationMethodToEnum = (
  applicationMethod?: string
): ApplicationMethod | undefined => {
  if (!applicationMethod) return undefined;
  const applicationMethodOption = applicationMethodOptions.find(
    option => option.label === applicationMethod
  );
  const key = applicationMethodOption?.value as keyof typeof ApplicationMethod;
  return key ? ApplicationMethod[key] : undefined;
};

export const mapApplicationMethodToValue = (
  applicationMethod?: string
): string => {
  if (!applicationMethod) return '';
  const applicationMethodOption = applicationMethodOptions.find(
    option => option.label === applicationMethod
  );
  return applicationMethodOption?.value ?? '';
};

export const transformRecommendationProductEntryToProduct = (
  product: RecommendationProductEntry
): Product => {
  const totalProductUnit =
    product.totalProductUnit || product.productRatePerAreaUnit || '';

  let productQuantityType = ProductQuantityType.totalProduct;
  if (product.productRatePerTankValue) {
    productQuantityType = ProductQuantityType.ratePerTank;
  } else if (product.productRatePerAreaValue) {
    productQuantityType = ProductQuantityType.ratePerAcre;
  }

  return {
    productId: product.productId,
    productName: product.productName || '',
    productQuantityType,
    priceValue: 0,
    priceUnit: totalProductUnit,
    totalCostValue: (product.totalProductValue || 0) * 0,
    totalCostUnit: totalProductUnit,
    appliedAreaValue: product.productAppliedAreaValue || 0,
    appliedAreaUnit: product.productAppliedAreaUnit || 'acre',
    appliedAreaCoveragePercent: (product.coveragePercent || 1) * 100,
    totalProductValue: product.totalProductValue || 0,
    totalProductUnit,
    ratePerAreaValue: product.productRatePerAreaValue || 0,
    ratePerAreaUnit: product.productRatePerAreaUnit || totalProductUnit,
    ratePerTankValue: product.productRatePerTankValue || 0,
    ratePerTankUnit: product.productRatePerTankUnit || totalProductUnit,
    applicationMethod: mapApplicationMethodToEnum(product.applicationMethod),
    targetPestNameIds: product.targetPestId
      ? [product.targetPestId.toString()]
      : [],
    custom: product.custom || false,
    isCarrier: false,
    isHidden: false
  };
};

export const transformRecommendationCropZonesToOperationPropertyVO = (
  recommendation: RecommendationVO,
  propertyLandingPageData: any
): OperationPropertyVO[] => {
  if (
    !recommendation?.cropZones?.length ||
    !propertyLandingPageData?.properties
  ) {
    return [];
  }

  const allFullCropZones = getCropZonesFromProperties(
    propertyLandingPageData.properties
  );

  return recommendation.cropZones
    .map((cropZone): OperationPropertyVO | null => {
      const fullMatch = allFullCropZones.find(
        (fullCropZone: any) => fullCropZone.id === cropZone.cropZoneIdValue
      );
      if (!fullMatch) return null;

      const area = fullMatch.reportedArea || fullMatch.boundaryArea || 0;
      const appliedArea = cropZone.areaValue || 0;
      let appliedAreaPercent = 0;
      if (cropZone.coveragePct && Number.isFinite(cropZone.coveragePct)) {
        appliedAreaPercent = Maths.multiplyRound(cropZone.coveragePct, 100);
      } else if (area) {
        appliedAreaPercent = Maths.multiplyRound(
          Maths.divideSafeRound(appliedArea, Maths.parseFloatRound(area, 2)),
          100
        );
      }

      return {
        farmId: fullMatch.propertyId || '',
        farmName: fullMatch.propertyName || '',
        farmArchived: fullMatch.propertyArchived || false,
        fieldId: fullMatch.fieldId || '',
        fieldName: fullMatch.fieldName || '',
        fieldGeometry: fullMatch.fieldGeometry || fullMatch.fieldPolygon,
        fieldArchived: fullMatch.fieldArchived || false,
        cropZoneId: cropZone.cropZoneIdValue || '',
        cropZoneName: fullMatch.name || cropZone.name || '',
        cropZoneGeometry: fullMatch.geometry,
        cropZoneArea: area,
        appliedArea,
        appliedAreaPercent,
        cropZoneArchived: fullMatch.archived || false,
        cropName: fullMatch.crop.name || '',
        cropId: cropZone.cropId || fullMatch.crop.id || ''
      };
    })
    .filter((entry): entry is OperationPropertyVO => entry !== null);
};

export const transformRecommendationCropZonesToProperty = (
  recommendation: RecommendationVO,
  propertyLandingPageData: any
): Property[] => {
  if (!recommendation?.cropZones?.length) {
    return [];
  }

  const allFullCropZones = propertyLandingPageData?.properties
    ? getCropZonesFromProperties(propertyLandingPageData.properties)
    : [];

  return recommendation.cropZones.map(cropZone => {
    const fullMatch = allFullCropZones.find(
      (fullCropZone: any) => fullCropZone.id === cropZone.cropZoneIdValue
    );
    const areaType = fullMatch?.boundaryArea ? 'BOUNDARY' : 'REPORTED';

    const coveragePercent =
      cropZone.coveragePct && Number.isFinite(cropZone.coveragePct)
        ? Maths.multiplyRound(cropZone.coveragePct, 100, 2)
        : recommendation.coveragePercent || 100;

    return {
      farmId: fullMatch?.propertyId || '',
      fieldId: fullMatch?.fieldId || '',
      cropZoneId: cropZone.cropZoneIdValue || '',
      areaValue: cropZone.areaValue || 0,
      areaUnit: cropZone.areaUnit || 'acre',
      areaType,
      coveragePercent
    } as Property;
  });
};

export const enrichApplicatorsAndAuthorizer = async (
  recommendation: RecommendationVO
): Promise<{
  enrichedApplicators: any[];
  authorizedBy: PersonCompany | undefined;
}> => {
  // Fetch applicators and authorizer data
  const applicatorIds =
    recommendation.applicators?.map(a => a.personId).filter(Boolean) || [];
  const applicatorCompanyIds =
    recommendation.applicators?.map(a => a.companyId).filter(Boolean) || [];
  const authorizerId = recommendation.authorisedById;
  const allPersonIds = [...applicatorIds];
  if (authorizerId) {
    allPersonIds.push(authorizerId);
  }

  const people = await getPeopleByIds(allPersonIds as string[]);
  const companies = await getCompaniesByIds(applicatorCompanyIds as string[]);

  // Create maps for easy lookup
  const peopleMap = new Map(people.map((p: any) => [p.id, p]));
  const companyMap = new Map(companies.map((c: any) => [c.id, c]));

  // Build complete applicators array
  const enrichedApplicators =
    recommendation.applicators?.map((app: any) => {
      const person = app.personId ? peopleMap.get(app.personId) : undefined;
      const company = app.companyId ? companyMap.get(app.companyId) : undefined;
      return {
        personId: app.personId,
        companyId: app.companyId,
        personName: person?.name,
        companyName: company?.name,
        licenseNumber: person?.licenseNumber,
        expirationDate: person?.expirationDateEpoch
          ? moment(new Date(person.expirationDateEpoch)).format('MMM dd, yyyy')
          : undefined
      };
    }) || [];

  // Build authorizer object
  const authorizerPerson = authorizerId ? peopleMap.get(authorizerId) : null;
  const authorizedBy: PersonCompany | undefined = authorizerPerson
    ? {
        id: authorizerPerson.id,
        name: authorizerPerson.name,
        type: authorizerPerson.type,
        licenseNumber: authorizerPerson.licenseNumber
      }
    : undefined;

  return { enrichedApplicators, authorizedBy };
};
